import { useContext } from 'react';
import { AppContext } from '@/contexts/AppContext';
import { SupervisorContext } from '@/contexts/SupervisorContext';
import { useActivePersona } from './useActivePersona';
import { useClientPersona } from './useClientPersona';
import type { Persona } from './types';

/** Presentationen väljer stjärnor/XP; serverns kontopolicy avgör godkännandekravet. */
export type StarValueMode = {
  enabled: boolean;
  asXp: boolean;
  needsApproval: boolean;
};

const DISABLED: StarValueMode = { enabled: false, asXp: false, needsApproval: false };

/**
 * Ren regel (inga hooks/sidoeffekter) — testbar likt resolveChildPresentation.
 *
 *   - Handledarläge: KLIENTENS persona avgör om steget visas (handledaren själv
 *     resolvar alltid 'adult'). Föräldern som skapar åt barnet sätter ett värde som
 *     är fastställt direkt — det är hen som godkänner. Okänd klientpersona → dolt.
 *   - Egen vy: barn OCH tonåring begär värdet av sin handledare. Personan avgör
 *     bara presentationen (stjärnor respektive XP), aldrig om det ska godkännas.
 *     Kontoflaggan speglar samma policy i schemat (V004) och kan bara skärpa den.
 */
export const resolveStarValueMode = (args: {
  isSupervising: boolean;
  ownPersona: Persona;
  clientPersona: Persona | null;
  starValueApprovalRequired?: boolean;
}): StarValueMode => {
  const persona = args.isSupervising ? args.clientPersona : args.ownPersona;
  if (persona !== 'child' && persona !== 'teen') {
    return DISABLED;
  }
  const isOwnView = !args.isSupervising;
  const isTeenOwnView = persona === 'teen' && isOwnView;
  return {
    enabled: true,
    asXp: isTeenOwnView,
    // persona är här redan avgränsad till 'child' | 'teen', och båda begär värdet i
    // egen vy. Handledaren fastställer sitt eget värde direkt - det är hen som godkänner.
    needsApproval: isOwnView,
  };
};

/** StarValueMode för det aktuella läget. Speglar CreateActivityPage:s persona-källor. */
export const useStarValuePersona = (): StarValueMode => {
  const { user } = useContext(AppContext);
  const { currentClient, isSupervising } = useContext(SupervisorContext);
  const ownProfile = useActivePersona({ currentClient, isSupervising });
  const { persona: clientPersona } = useClientPersona(currentClient);

  return resolveStarValueMode({
    isSupervising,
    ownPersona: ownProfile.persona,
    clientPersona,
    starValueApprovalRequired: user?.starValueApprovalRequired,
  });
};
